import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { api } from 'api/_init'
import { UPDATE_PARTNER, UPDATE_PERMISSIONS, UPDATE_FEATURES } from 'reducers/user'
import { useToast } from 'components/Shared/AppToast/ToastProvider'

/**
 * The partnerId/modal/select-account state shared by the desktop and mobile
 * account-switcher triggers — they render very differently (desktop rides on
 * .main-nav's collapse/expand row markup, mobile is always "expanded"), but
 * must never drift on the actual account-switching behaviour (localStorage +
 * UPDATE_PARTNER + reload), so that part lives here once.
 */
export function useAccountSwitcher() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { addNotification } = useToast()
  const partnerId = useSelector((state) => state.user.partnerId)
  // Same signal PartnerSelect.jsx (the login-time picker) already gates on —
  // the cognito JWT's partner groups, synchronously in state, no extra fetch.
  const cognitoGroups = useSelector((state) => state.user.cognitoGroups) ?? []
  const hasMultipleAccounts = cognitoGroups.length > 1
  const [showModal, setShowModal] = useState(false)
  const [switchingAccountId, setSwitchingAccountId] = useState(null)

  const selectAccount = useCallback(
    async (accountId) => {
      if (accountId === partnerId) {
        setShowModal(false)
        return
      }

      const previousPartnerId = partnerId
      setSwitchingAccountId(accountId)
      // UPDATE_PARTNER has to land before the requests below: api/_init.js's
      // transformRequest reads X-Preferred-Partner-Id straight off the store,
      // unconditionally overwriting any header passed in through request
      // config — so there's no way to "probe" the candidate partner's
      // permissions without actually switching to it first. If the fetch
      // below fails, we dispatch back to previousPartnerId to undo this.
      dispatch({ type: UPDATE_PARTNER, payload: accountId })

      try {
        const [permissionsRes, featuresRes] = await Promise.all([
          api.get('/permissions'),
          api.get('/features'),
        ])
        dispatch({ type: UPDATE_PERMISSIONS, payload: permissionsRes.data ?? [] })
        dispatch({ type: UPDATE_FEATURES, payload: featuresRes.data ?? {} })
        localStorage.setItem('clientId', accountId)
        // Fresh permissions/features are already dispatched (and persisted to
        // localStorage['reduxState'] via App.js's store.subscribe) by this
        // point, so a plain reload — staying on the current URL — is safe:
        // PrivateRoute re-checks the current route's permission on remount
        // against this now-current data and redirects on its own if the new
        // partner can't access it. No need to duplicate that check here.
        window.location.reload()
      } catch (e) {
        dispatch({ type: UPDATE_PARTNER, payload: previousPartnerId })
        setSwitchingAccountId(null)
        addNotification({ message: t('AccountSwitcher.switchError'), variant: 'error' })
      }
    },
    [dispatch, partnerId, addNotification, t],
  )

  return {
    partnerId,
    hasMultipleAccounts,
    showModal,
    setShowModal,
    switchingAccountId,
    selectAccount,
  }
}
